import _ from 'lodash';
import store, { StoreType } from 'store2';
import { toProxy, watchUpdates } from '../watcher';
import { IStoreWithData, INamespaceOptions, IStores } from './store-intf';
import { indexedDbUtils } from './indexed-db-utils';
import { Log } from '../log/Log';

const localStoreMap: Record<string, IStoreWithData<Record<string, any>>> = {}
function createLocalStore<T extends Record<string, any>>(ns: string, opts?: INamespaceOptions<T>): IStoreWithData<T> {
    let unsub: (() => void) | undefined
    let ss = localStoreMap[ns]
    if (ss) {
        return ss as IStoreWithData<T>
    }
    const s = store.namespace(ns);
    if (opts?.forceReset && opts?.defData) {
        s.setAll(opts?.defData)
    }
    if (_.isObject(opts?.defData) && Object.keys(s.getAll()).length == 0) {
        s.setAll(opts?.defData)
    }
    let data: T = createData();
    function createData(): T {
        unsub?.()
        unsub = undefined
        try {
            const d = (s.getAll()) as T;
            const data = toProxy((d && Object.keys(d).length > 0 ? d : {}) as T);
            watchData(data);
            return data;
        } catch (e) {
            console.error(e)
            // 之前写入过错误数据，譬如写入过s，s没有toString的方法，在proxy那边会报错
            s.setAll({})
            return toProxy({}) as T
        }
    }
    let requestAnimationFrameId: number = -1;
    function watchData(data: T) {
        unsub = watchUpdates(data, () => {
            if (requestAnimationFrameId > -1) return;
            requestAnimationFrameId = window.requestAnimationFrame(() => {
                requestAnimationFrameId = -1;
                try {
                    store.namespace(ns).setAll(data)
                } catch (e) {
                    alert(e)
                    console.error(e)
                }
            })
        });
    }
    const res = {
        data,
        store: {
            getData: () => data,
            read<U>(key: keyof T, defaultValue?: U): U | undefined {
                return s.get(key, defaultValue)
            },
            write<K extends keyof T>(key: K, value: T[K], override?: boolean) {
                // s.set(key, value, override)
                // 这样得解决大数据写入的问题，所以最终可能是watch那边得增加根key变更的数据
                data[key] = value;
                // store.namespace(ns).set(key, value, override)
            },
            clearAll(): void {
                store.namespace(ns).clearAll();
                const keys = Object.keys(data);
                for (const key of keys) {
                    delete data[key];
                }
            },
            setAll(d: Partial<T>) {
                store.namespace(ns).setAll(d)
                res.store.clearAll();
                const keys = Object.keys(d) as (keyof T)[];
                for (const key of keys) {
                    (data as any)[key] = d[key];
                }
            },
            remove(key: string): void {
                store.namespace(ns).remove(key)
                delete data[key]
            }
        }
    }
    localStoreMap[ns] = res;
    return res;
}
function createJavaStore<T extends Record<string, any>>(ns: string, opts?: INamespaceOptions<T>): IStoreWithData<T> {
    const log = new Log(false, 'createJavaStore_fn ns= ' + ns);
    log.pause = true;
    // log.pause = ns != `@fanfanlo/scripts/emergency-alarm/script`;
    let unsub: (() => void) | undefined;
    let data: T = createData();
    function createData() {
        unsub?.()
        unsub = undefined
        const raw = getStoresNamespace<T>(ns, opts) || {} as T;
        const o = raw || {} as T;

        mergeDefault();
        async function mergeDefault() {
            log.log('mergeDefaultdata check opts', ns, raw);
            if (!opts) return;
            log.log('mergeDefaultdata check mergeDefdata must is true', opts.mergeDefData);
            if (opts.mergeDefData !== true) return;
            log.log('start mergeDefaultdata', ns)
            if (!raw || _.isArray(raw) || !_.isObject(raw)) return;
            log.log('mergeDefaultdata check defdata');
            if (!opts.defData) return;
            log.log('mergeDefaultdata check mergedefdata');
            if (opts.forceReset) return;
            log.log('mergeDefaultdata check o');
            if (!o) return;
            const defData = opts.defData
            log.log('real start mergeDefData', defData)
            let keys = Object.keys(defData);
            let updated = false;
            for (let i = 0; i < keys.length; i++) {
                let key = keys[i];
                if (key in o) continue;
                log.log('add key', key, defData[key]);
                (o as any)[key] = defData[key];
                updated = true;
            }
            keys = Object.keys(o);
            for (let i = 0; i < keys.length; i++) {
                let key = keys[i];
                if (key in defData) continue;
                log.log('del key', key);
                delete o[key];
                updated = true;
            }
            log.log('after merge o=', o);
            if (updated) {
                is?.write(ns, o)
            }
        }
        const data = toProxy(o);
        watchData(data);
        return data;
    }
    let isClearAll = false;

    let callingNextFrame = false;
    function callNextFrame(){
        if(callingNextFrame)return;
        callingNextFrame = true;
        window.requestAnimationFrame(()=>{
            callingNextFrame = false;
            is?.write(ns, data)
        })
    }
    function watchData(data: T) {
        unsub = watchUpdates(data, () => {
            if (isClearAll) return;
            // console.log('java watch data', 'ns=', ns, 'data=', data)
            log.log(`java watch data`, `ns=`, ns, `data=`, data)
            // is?.write(ns, data)
            callNextFrame()
        });
    }
    const res = {
        data,
        store: {
            getData: () => data,
            read<U>(key: keyof T, defaultValue?: U): U | undefined {
                return data[key] ?? defaultValue;
            },
            write<K extends keyof T>(key: K, value: T[K], override?: boolean) {
                data[key] = value;
            },
            clearAll(): void {
                is?.write(ns, '')
                const keys = Object.keys(data);
                isClearAll = true;
                for (const key of keys) {
                    delete data[key];
                }
                isClearAll = false;
            },
            setAll(d: Partial<T>) {
                is?.write(ns, d)
                res.store.clearAll();
                const keys = Object.keys(d);
                isClearAll = true;
                for (const key of keys) {
                    (data as any)[key] = d[key];
                }
                isClearAll = false;
            },
            remove(key: string): void {
                delete data[key]
            }
        }
    }
    return res;
}
let is: IStores<Record<string, any>> | undefined = undefined;
function setStoresProxy(s: IStores<Record<string, any>>) {
    s.ns = toProxy(s.ns)
    is = s;
}
function getStoresNamespace<T extends Record<string, any>>(ns: string, opts?: INamespaceOptions<T>): T | undefined {
    if (!is) return undefined;
    let s = is.ns[ns];
    if (!s) {
        s = is.read(ns);
        if (s) {
            // is.ns[ns] = toProxy(s);
        } else {
            s = is.ns[ns] = (opts?.defData || {}) as T;
        }
    }
    if (opts?.forceReset && opts.defData) {
        let keys = Object.keys(s);
        for (const key of keys) {
            delete s[key];
        }
        keys = Object.keys(opts.defData);
        for (const key of keys) {
            s[key] = opts.defData[key];
        }
        console.log('getStoresNamespace_fn force reset', ns,)
        is.write(ns, opts.defData)
    }
    is.ns[ns] = toProxy(s)
    return is.ns[ns];
}
function namespace(ns: string) {
    const res = is ? createJavaStore(ns) : createLocalStore(ns);
    return res.store
}

async function getNamespaceStoreWithData<T extends Record<string, any>>(ns: string, opts?: INamespaceOptions<T>): Promise<IStoreWithData<T>> {

    // const res = is? createJavaStore(ns, opts):createLocalStore(ns, opts);
    const res = is ? createJavaStore(ns, opts) : indexedDbUtils.getIndexedDbStore(ns, opts);
    return res;
}

//页面状态数据应该存在store2里即可
//script等数据应该存在安卓里，如果是安卓环境的话。
export const storeUtils = {
    //获取一个命名空间下的数据对象，本地会从localstorage里读取，
    //安卓环境会从安卓里读取
    namespace,
    getNamespaceStoreWithData,
    setStoresProxy,
    // 这个可以用来侦听子级字段变更以便写入localStorage
    // 不然store2普通的set是无法在子级对象变更时进行写入的。
    createLocalStore,
    //特殊需求的store还是从store2来,譬如大数据变更的情况，还是直接操作方便。
    createStore2: (ns: string) => store.namespace(ns)
}